import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Form, Card, Row, Col, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAngleLeft, faSave, faImage, faTools, faUsers,
    faShieldAlt, faPlus, faBuildingColumns
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Swal from "sweetalert2";
import useFetch from "../../../hooks/useFetch";
import LocalizacaoSelector from "../../../components/administracao/LocalizacaoSelector";

// Únicos valores aceitos pelo banco (CHECK constraint) — mantidos iguais aos
// definidos em backend/src/models/Fotografia.js para nunca dar 400 ao salvar.
const OPCOES_TECNICA = ["Gelatina/Prata", "Digital", "Daguerreótipo", "Outro"];
const OPCOES_COR = ["P&B", "Colorida", "Sépia", "Outro"];
const OPCOES_SUPORTE = ["Papel Fotográfico", "Digital", "Vidro", "Outro"];
const OPCOES_CONSERVACAO = ["Bom", "Regular", "Ruim", "Péssimo"];
const OPCOES_ORIGEM = ["Doação", "Compra", "Produção própria", "Outro"];
const OPCOES_TIPO_ACERVO = ["Pessoa física", "Departamento", "Instituição", "Outro"];

const API_URL = process.env.REACT_APP_API_URL;

const dadosVazios = {
    numero_tombo: "",
    codigo_referencia: "",
    titulo_atribuido: "",
    data_producao: "",
    ano_producao: "",
    decada_producao: "",
    local_producao: "",
    descricao: "",
    contexto_historico: "",
    origem_proveniencia: "",
    data_entrada_acervo: "",
    numero_inventario_associado: "",
    cor: "",
    tecnica: "",
    suporte: "",
    estado_conservacao: "",
    status_fotografia: "ativo",
    id_autor: null,
    id_direito_autoral: null,
    id_acervo_detentor: null,
    id_localizacao: null,
    id_evento: null,
    id_categoria: null,
    id_subcategoria: null,
};

export default function Fotografia() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { get, post, put } = useFetch();

    const [showPessoaModal, setShowPessoaModal] = useState(false);
    const [showAutorModal, setShowAutorModal] = useState(false);
    const [showEventoModal, setShowEventoModal] = useState(false);
    const [showDireitoModal, setShowDireitoModal] = useState(false);
    const [showAcervoModal, setShowAcervoModal] = useState(false);
    const [showCategoriaModal, setShowCategoriaModal] = useState(false);

    const [novoAutor, setNovoAutor] = useState({ nome: "", nacionalidade: "", data_nascimento: "", data_falecimento: "" });
    const [novaPessoa, setNovaPessoa] = useState({ nome: "", cargo_funcao: "", instituicao: "", departamento: "", periodo_atuacao: "", observacoes: "" });
    const [novoEvento, setNovoEvento] = useState({ nome_evento: "", tipo_evento: "", local_evento: "", data_inicio: "", data_fim: "", descricao: "" });
    const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState({ sala: "", estante: "", prateleira: "", caixa: "", envelope_pasta: "", id_localizacao: null });
    const [novoDireito, setNovoDireito] = useState({ nome_direito: "", descricao: "", permite_reproducao: false });
    const [novoAcervo, setNovoAcervo] = useState({ nome: "", tipo: "Instituição", observacoes: "" });
    const [novaCategoria, setNovaCategoria] = useState({ nome_categoria: "" });
    const [novaSubcategoria, setNovaSubcategoria] = useState({ id_categoria: "", nome_subcategoria: "" });

    const [state, setState] = useState({
        acao: location.state?.acao || (id === "criar" ? "Criar" : "Visualizar"),
        dados: dadosVazios,
        pessoasSelecionadas: [],
        palavrasChave: ["", "", ""],
        loading: id !== "criar",
    });

    const [opcoes, setOpcoes] = useState({
        autores: [], eventos: [], categorias: [], subcategorias: [], direitos: [], pessoas: [], acervos: [],
    });

    const [imagemPreview, setImagemPreview] = useState(null);
    const [novoArquivoImagem, setNovoArquivoImagem] = useState(null);

    const somenteLeitura = state.acao === "Visualizar";

    // --- Carregamento das opções dos selects ---
    const paraOpcoes = (lista, chaveId, chaveLabel) =>
        (lista ?? []).map((item) => ({ value: item[chaveId], label: item[chaveLabel] }));

    const carregarOpcoesBase = useCallback(async () => {
        const [autoresRes, eventosRes, categoriasRes, direitosRes, pessoasRes, acervosRes] = await Promise.all([
            get("/autorfotografo?page=1&pageSize=1000"),
            get("/eventodocumentado?page=1&pageSize=1000"),
            get("/categoriafotografia?page=1&pageSize=1000"),
            get("/direitoautoral?page=1&pageSize=1000"),
            get("/pessoaretratada?page=1&pageSize=1000"),
            get("/acervodetentor?page=1&pageSize=1000"),
        ]);

        setOpcoes((prev) => ({
            ...prev,
            autores: autoresRes.ok ? paraOpcoes(autoresRes.result.data, "id_autor", "nome") : prev.autores,
            eventos: eventosRes.ok ? paraOpcoes(eventosRes.result.data, "id_evento", "nome_evento") : prev.eventos,
            categorias: categoriasRes.ok ? paraOpcoes(categoriasRes.result.data, "id_categoria", "nome_categoria") : prev.categorias,
            direitos: direitosRes.ok ? paraOpcoes(direitosRes.result.data, "id_direito", "nome_direito") : prev.direitos,
            pessoas: pessoasRes.ok ? paraOpcoes(pessoasRes.result.data, "id_pessoa", "nome") : prev.pessoas,
            acervos: acervosRes.ok ? paraOpcoes(acervosRes.result.data, "id_acervo_detentor", "nome") : prev.acervos,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarSubcategorias = useCallback(async (id_categoria) => {
        if (!id_categoria) {
            setOpcoes((prev) => ({ ...prev, subcategorias: [] }));
            return;
        }
        let response = await get(`/subcategoriafotografia?id_categoria=${id_categoria}&page=1&pageSize=1000`);
        setOpcoes((prev) => ({
            ...prev,
            subcategorias: response.ok ? paraOpcoes(response.result.data, "id_subcategoria", "nome_subcategoria") : [],
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Busca o binário da imagem manualmente (não dá pra usar <img src="/fotografia/1/imagem">
    // direto porque a rota exige o header Authorization, que a tag <img> não envia).
    const carregarPreviewImagem = useCallback(async (id_fotografia) => {
        try {
            const token = localStorage.getItem("authToken");
            let resp = await fetch(`${API_URL}/fotografia/${id_fotografia}/imagem`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
                let blob = await resp.blob();
                setImagemPreview(URL.createObjectURL(blob));
            } else {
                setImagemPreview(null);
            }
        } catch (error) {
            console.error("Erro ao carregar preview da imagem:", error);
            setImagemPreview(null);
        }
    }, []);

    useEffect(() => {
        carregarOpcoesBase();

        if (id === "criar") {
            setState((prev) => ({ ...prev, acao: "Criar", loading: false }));
            setLocalizacaoSelecionada({ sala: "", estante: "", prateleira: "", caixa: "", envelope_pasta: "", id_localizacao: null });
            return;
        }

        async function carregarFotografia() {
            let response = await get(`/fotografia/${id}`);
            if (response.ok && response.result.data) {
                const f = response.result.data;
                setState((prev) => ({
                    ...prev,
                    dados: {
                        numero_tombo: f.numero_tombo ?? "",
                        codigo_referencia: f.codigo_referencia ?? "",
                        titulo_atribuido: f.titulo_atribuido ?? "",
                        data_producao: f.data_producao ?? "",
                        ano_producao: f.ano_producao ?? "",
                        decada_producao: f.decada_producao ?? "",
                        local_producao: f.local_producao ?? "",
                        descricao: f.descricao ?? "",
                        contexto_historico: f.contexto_historico ?? "",
                        origem_proveniencia: f.origem_proveniencia ?? "",
                        data_entrada_acervo: f.data_entrada_acervo ?? "",
                        numero_inventario_associado: f.numero_inventario_associado ?? "",
                        cor: f.cor ?? "",
                        tecnica: f.tecnica ?? "",
                        suporte: f.suporte ?? "",
                        estado_conservacao: f.estado_conservacao ?? "",
                        status_fotografia: f.status_fotografia ?? "ativo",
                        id_autor: f.id_autor ?? null,
                        id_direito_autoral: f.id_direito_autoral ?? null,
                        id_acervo_detentor: f.id_acervo_detentor ?? null,
                        id_localizacao: f.id_localizacao ?? null,
                        id_evento: f.id_evento ?? null,
                        id_categoria: f.id_categoria ?? null,
                        id_subcategoria: f.id_subcategoria ?? null,
                    },
                    pessoasSelecionadas: (f.PessoaRetratadas ?? []).map((p) => ({ value: p.id_pessoa, label: p.nome })),
                    palavrasChave: (() => {
                        const existentes = (f.PalavraChaveFotografia ?? f.PalavraChaveFotografias ?? []).map((p) => p.palavra);
                        return [existentes[0] ?? "", existentes[1] ?? "", existentes[2] ?? ""];
                    })(),
                    loading: false,
                }));
                if (f.id_categoria) carregarSubcategorias(f.id_categoria);
                if (f.Localizacao) {
                    setLocalizacaoSelecionada({
                        sala: f.Localizacao.sala ?? "",
                        estante: f.Localizacao.estante ?? "",
                        prateleira: f.Localizacao.prateleira ?? "",
                        caixa: f.Localizacao.caixa ?? "",
                        envelope_pasta: f.Localizacao.envelope_pasta ?? "",
                        id_localizacao: f.Localizacao.id_localizacao ?? null,
                    });
                }
                carregarPreviewImagem(id);
            } else {
                setState((prev) => ({ ...prev, loading: false }));
                Swal.fire({ title: "Fotografia não encontrada", icon: "error", confirmButtonColor: "#587b7f" });
            }
        }
        carregarFotografia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    function alterarCampo(event) {
        const { name, value } = event.target;
        setState((prev) => ({ ...prev, dados: { ...prev.dados, [name]: value } }));
    }

    // Data de Produção / Ano / Década estão em cascata: o campo mais
    // específico que o usuário preencher já deriva os mais genéricos, pra
    // não deixar Ano/Década em branco (o que fazia o filtro de década na
    // busca pública não encontrar fotos que só tinham a data completa
    // preenchida — ele só olhava pra decada_producao).
    function alterarDataProducao(event) {
        const valor = event.target.value; // "YYYY-MM-DD" ou ""
        setState((prev) => {
            const novosDados = { ...prev.dados, data_producao: valor };
            if (valor) {
                const ano = parseInt(valor.slice(0, 4), 10);
                if (!Number.isNaN(ano)) {
                    novosDados.ano_producao = ano;
                    novosDados.decada_producao = Math.floor(ano / 10) * 10;
                }
            }
            return { ...prev, dados: novosDados };
        });
    }

    function alterarAnoProducao(event) {
        const valor = event.target.value;
        setState((prev) => {
            const novosDados = { ...prev.dados, ano_producao: valor };
            const ano = parseInt(valor, 10);
            if (valor && !Number.isNaN(ano)) {
                novosDados.decada_producao = Math.floor(ano / 10) * 10;
            }
            return { ...prev, dados: novosDados };
        });
    }

    function alterarSelect(campo, opcaoSelecionada) {
        setState((prev) => ({ ...prev, dados: { ...prev.dados, [campo]: opcaoSelecionada?.value ?? null } }));
        if (campo === "id_categoria") {
            carregarSubcategorias(opcaoSelecionada?.value);
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_subcategoria: null } }));
        }
    }

    function selecionarArquivoImagem(event) {
        const arquivo = event.target.files?.[0];
        if (!arquivo) return;
        setNovoArquivoImagem(arquivo);
        setImagemPreview(URL.createObjectURL(arquivo));
    }

    async function salvar() {
        // Número de tombo é opcional (nem toda fotografia já foi tombada);
        // só o título continua obrigatório.
        if (!state.dados.titulo_atribuido) {
            Swal.fire({ title: "Preencha o título atribuído", icon: "warning", confirmButtonColor: "#587b7f" });
            return;
        }

        try {
            let idFotografia = id;
            let response;
            if (state.acao === "Criar") {
                response = await post("/fotografia", state.dados);
            } else {
                response = await put(`/fotografia/${id}`, state.dados);
            }

            if (!(response.status === 200 || response.status === 201)) {
                Swal.fire({
                    title: "Não foi possível salvar a fotografia",
                    text: response.result?.message ?? "Erro ao salvar",
                    icon: "error",
                    confirmButtonColor: "#587b7f",
                });
                return;
            }

            if (state.acao === "Criar") {
                idFotografia = response.result.data.id_fotografia;
            }

            // Sincroniza pessoas retratadas
            await put(`/fotografia/${idFotografia}/pessoas`, {
                id_pessoa: state.pessoasSelecionadas.map((p) => p.value),
            });

            // Sincroniza palavras-chave (texto livre, cria as que não existirem)
            await put(`/fotografia/${idFotografia}/palavraschave`, {
                palavras: state.palavrasChave,
            });

            // Envia a imagem, se uma nova foi escolhida
            if (novoArquivoImagem) {
                const formData = new FormData();
                formData.append("imagem", novoArquivoImagem);
                let respImagem = await post(`/fotografia/${idFotografia}/imagem`, formData, null, "multipart/form-data");
                if (!(respImagem.status === 200)) {
                    Swal.fire({
                        title: "Fotografia salva, mas a imagem não pôde ser enviada",
                        text: respImagem.result?.message ?? "",
                        icon: "warning",
                        confirmButtonColor: "#587b7f",
                    });
                    navigate("/admin/iconografico/fotografias");
                    return;
                }
            }

            Swal.fire({ title: "Fotografia Salva!", icon: "success", confirmButtonColor: "#587b7f" });
            navigate("/admin/iconografico/fotografias");
        } catch (error) {
            console.error("Erro no salvar():", error);
            Swal.fire({ title: "Erro inesperado ao salvar", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    // --- Cadastro rápido (modais "+ Novo") ---
    async function salvarNovoAutor() {
        let response = await post("/autorfotografo", novoAutor);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_autor: response.result.data.id_autor } }));
            setShowAutorModal(false);
            setNovoAutor({ nome: "", nacionalidade: "", data_nascimento: "", data_falecimento: "" });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar o autor", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function salvarNovaPessoa() {
        let response = await post("/pessoaretratada", novaPessoa);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({
                ...prev,
                pessoasSelecionadas: [...prev.pessoasSelecionadas, { value: response.result.data.id_pessoa, label: response.result.data.nome }],
            }));
            setShowPessoaModal(false);
            setNovaPessoa({ nome: "", cargo_funcao: "", instituicao: "", departamento: "", periodo_atuacao: "", observacoes: "" });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar a pessoa", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function salvarNovoEvento() {
        let response = await post("/eventodocumentado", novoEvento);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_evento: response.result.data.id_evento } }));
            setShowEventoModal(false);
            setNovoEvento({ nome_evento: "", tipo_evento: "", local_evento: "", data_inicio: "", data_fim: "", descricao: "" });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar o evento", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function salvarNovoDireito() {
        let response = await post("/direitoautoral", novoDireito);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_direito_autoral: response.result.data.id_direito } }));
            setShowDireitoModal(false);
            setNovoDireito({ nome_direito: "", descricao: "", permite_reproducao: false });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar o direito autoral", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function salvarNovoAcervo() {
        if (!novoAcervo.nome) {
            Swal.fire({ title: "Preencha o nome do acervo", icon: "warning", confirmButtonColor: "#587b7f" });
            return;
        }
        let response = await post("/acervodetentor", novoAcervo);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_acervo_detentor: response.result.data.id_acervo_detentor } }));
            setShowAcervoModal(false);
            setNovoAcervo({ nome: "", tipo: "Instituição", observacoes: "" });
        } else if (response.status === 401) {
            // Token expirado/inválido: avisar pra relogar em vez de deixar
            // parecer que o cadastro do acervo está quebrado.
            Swal.fire({
                title: "Sessão expirada",
                text: response.result?.message ?? "Faça login novamente para continuar.",
                icon: "warning",
                confirmButtonColor: "#587b7f",
            });
        } else {
            Swal.fire({
                title: "Não foi possível cadastrar o acervo",
                text: response.result?.message ?? "",
                icon: "error",
                confirmButtonColor: "#587b7f",
            });
        }
    }

    async function salvarNovaCategoria() {
        let response = await post("/categoriafotografia", novaCategoria);
        if (response.status === 201) {
            await carregarOpcoesBase();
            setState((prev) => ({ ...prev, dados: { ...prev.dados, id_categoria: response.result.data.id_categoria } }));
            setNovaCategoria({ nome_categoria: "" });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar a categoria", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function salvarNovaSubcategoria() {
        if (!novaSubcategoria.id_categoria) {
            Swal.fire({ title: "Escolha a categoria pai", icon: "warning", confirmButtonColor: "#587b7f" });
            return;
        }
        let response = await post("/subcategoriafotografia", novaSubcategoria);
        if (response.status === 201) {
            await carregarSubcategorias(novaSubcategoria.id_categoria);
            setState((prev) => ({
                ...prev,
                dados: { ...prev.dados, id_categoria: parseInt(novaSubcategoria.id_categoria, 10), id_subcategoria: response.result.data.id_subcategoria },
            }));
            setShowCategoriaModal(false);
            setNovaSubcategoria({ id_categoria: "", nome_subcategoria: "" });
        } else {
            Swal.fire({ title: "Não foi possível cadastrar a subcategoria", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    const selectStyles = {
        control: (base, stateProps) => ({
            ...base,
            borderRadius: '10px',
            borderColor: '#e2e8f0',
            minHeight: '44px',
            backgroundColor: stateProps.isDisabled ? '#e9ecef' : base.backgroundColor,
        }),
        menu: (base) => ({ ...base, zIndex: 5 }),
        // menuPortal: o menu é renderizado no <body> (menuPortalTarget abaixo),
        // então não é mais cortado pelo card/modal em que o Select está nem
        // aparece por baixo da navbar. z-index alinhado com scss/base/responsivo.scss.
        menuPortal: (base) => ({ ...base, zIndex: 1080 }),
        option: (base, stateProps) => ({
            ...base,
            backgroundColor: stateProps.isSelected ? '#587b7f' : stateProps.isFocused ? '#e8f0f1' : '#fff',
            color: stateProps.isSelected ? '#fff' : '#2c3e50',
        }),
        singleValue: (base) => ({ ...base, color: '#2c3e50' }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e8f0f1' }),
        multiValueLabel: (base) => ({ ...base, color: '#2c3e50' }),
        multiValueRemove: (base) => ({ ...base, color: '#587b7f', ':hover': { backgroundColor: '#587b7f', color: '#fff' } }),
        placeholder: (base) => ({ ...base, color: '#6c757d' }),
    };

    const LabelNovo = ({ label, onClick }) => (
        <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="label-admin mb-0">{label}</Form.Label>
            {!somenteLeitura && (
                <Button variant="link" className="text-teal p-0 text-decoration-none small fw-bold" onClick={onClick} style={{ fontSize: '0.75rem' }}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Novo
                </Button>
            )}
        </div>
    );

    const encontrarOpcao = (lista, valor) => lista.find((o) => o.value === valor) ?? null;

    if (state.loading) {
        return (
            <div className="admin-page-container text-center py-5">
                <div className="spinner-border text-teal"></div>
            </div>
        );
    }

    return (
        <div className="admin-page-container">
            <div className="mb-4">
                <Link to="/admin/iconografico/fotografias" className="btn-back-modern">
                    <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Voltar ao Acervo
                </Link>
            </div>

            <Card className="filter-card-admin">
                <Card.Body className="p-4 p-md-5">
                    <div className="section-title-modern mb-5">
                        <div className="title-accent"></div>
                        <div>
                            <h2 className="admin-title">{state.acao} Registro Fotográfico</h2>
                            <p className="admin-subtitle small mb-0">Documentação técnica iconográfica</p>
                        </div>
                    </div>

                    <Form>
                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faImage} className="me-2 text-teal" />
                            <span>Identificação e Imagem Principal</span>
                        </div>
                        <Row className="g-4 mb-5">
                            <Col lg={4}>
                                <div className="p-3 border rounded-4 text-center bg-light">
                                    {imagemPreview ? (
                                        <img src={imagemPreview} alt="Preview" className="img-fluid rounded-3 mb-3" style={{ maxHeight: '200px', objectFit: 'contain' }} />
                                    ) : (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center rounded-3 mb-3 border border-2 border-dashed text-muted"
                                            style={{ height: '200px', borderStyle: 'dashed' }}
                                        >
                                            <FontAwesomeIcon icon={faImage} size="2x" className="mb-2 opacity-50" />
                                            <span className="small">Nenhuma imagem cadastrada</span>
                                        </div>
                                    )}
                                    {!somenteLeitura && (
                                        <>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/tiff,image/webp"
                                                id="input-imagem-fotografia"
                                                style={{ display: 'none' }}
                                                onChange={selecionarArquivoImagem}
                                            />
                                            <Button
                                                variant="outline-secondary" size="sm" className="rounded-pill w-100"
                                                onClick={() => document.getElementById('input-imagem-fotografia').click()}
                                            >
                                                Alterar Fotografia
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </Col>
                            <Col lg={8}>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Label className="label-admin">Número de Tombo</Form.Label>
                                        <Form.Control className="input-moderno" name="numero_tombo" value={state.dados.numero_tombo} onChange={alterarCampo} disabled={somenteLeitura} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label className="label-admin">Código de Referência</Form.Label>
                                        <Form.Control className="input-moderno" name="codigo_referencia" value={state.dados.codigo_referencia} onChange={alterarCampo} disabled={somenteLeitura} />
                                    </Col>
                                    <Col md={12}>
                                        <Form.Label className="label-admin">* Título Atribuído</Form.Label>
                                        <Form.Control className="input-moderno" name="titulo_atribuido" value={state.dados.titulo_atribuido} onChange={alterarCampo} disabled={somenteLeitura} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faTools} className="me-2 text-teal" />
                            <span>Dados de Produção e Característica Técnica</span>
                        </div>
                        <Row className="g-4 mb-5">
                            <Col md={6}>
                                <LabelNovo label="Autor / Fotógrafo" onClick={() => setShowAutorModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.autores} styles={selectStyles} placeholder="Selecione o autor..." isClearable
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.autores, state.dados.id_autor)}
                                    onChange={(op) => alterarSelect("id_autor", op)}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="label-admin">Local de Produção</Form.Label>
                                <Form.Control className="input-moderno" name="local_producao" placeholder="Cidade, Estado..." value={state.dados.local_producao} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="label-admin">Data de Produção</Form.Label>
                                <Form.Control type="date" className="input-moderno" name="data_producao" value={state.dados.data_producao ?? ""} onChange={alterarDataProducao} disabled={somenteLeitura} />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="label-admin">Ano Produção</Form.Label>
                                <Form.Control type="number" className="input-moderno" name="ano_producao" placeholder="Ex: 1911 (preenchido automaticamente se souber a data completa)" value={state.dados.ano_producao} onChange={alterarAnoProducao} disabled={somenteLeitura} />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="label-admin">Década</Form.Label>
                                <Form.Control type="number" className="input-moderno" name="decada_producao" placeholder="Ex: 1910 (preenchido automaticamente se souber o ano)" value={state.dados.decada_producao} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>

                            <Col md={3}>
                                <Form.Label className="label-admin">Técnica</Form.Label>
                                <Form.Select className="input-moderno" name="tecnica" value={state.dados.tecnica} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="">Selecione...</option>
                                    {OPCOES_TECNICA.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Cor</Form.Label>
                                <Form.Select className="input-moderno" name="cor" value={state.dados.cor} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="">Selecione...</option>
                                    {OPCOES_COR.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Suporte</Form.Label>
                                <Form.Select className="input-moderno" name="suporte" value={state.dados.suporte} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="">Selecione...</option>
                                    {OPCOES_SUPORTE.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Conservação</Form.Label>
                                <Form.Select className="input-moderno" name="estado_conservacao" value={state.dados.estado_conservacao} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="">Selecione...</option>
                                    {OPCOES_CONSERVACAO.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>

                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faUsers} className="me-2 text-teal" />
                            <span>Análise de Conteúdo e Indexação</span>
                        </div>
                        <Row className="g-4 mb-5">
                            <Col md={4}>
                                <Form.Label className="label-admin">Palavra-Chave 1</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Retrato"
                                    value={state.palavrasChave[0]}
                                    onChange={(e) => setState((prev) => ({ ...prev, palavrasChave: [e.target.value, prev.palavrasChave[1], prev.palavrasChave[2]] }))}
                                    disabled={somenteLeitura}
                                    maxLength={100}
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="label-admin">Palavra-Chave 2</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Laboratório"
                                    value={state.palavrasChave[1]}
                                    onChange={(e) => setState((prev) => ({ ...prev, palavrasChave: [prev.palavrasChave[0], e.target.value, prev.palavrasChave[2]] }))}
                                    disabled={somenteLeitura}
                                    maxLength={100}
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="label-admin">Palavra-Chave 3</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Formação"
                                    value={state.palavrasChave[2]}
                                    onChange={(e) => setState((prev) => ({ ...prev, palavrasChave: [prev.palavrasChave[0], prev.palavrasChave[1], e.target.value] }))}
                                    disabled={somenteLeitura}
                                    maxLength={100}
                                />
                            </Col>

                            <Col md={12}>
                                <LabelNovo label="Pessoas Retratadas (Múltipla Seleção)" onClick={() => setShowPessoaModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    isMulti options={opcoes.pessoas} styles={selectStyles} placeholder="Pesquise por nomes..."
                                    isDisabled={somenteLeitura}
                                    value={state.pessoasSelecionadas}
                                    onChange={(ops) => setState((prev) => ({ ...prev, pessoasSelecionadas: ops ?? [] }))}
                                />
                            </Col>
                            <Col md={6}>
                                <LabelNovo label="Evento Documentado" onClick={() => setShowEventoModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.eventos} styles={selectStyles} placeholder="Selecione o evento..." isClearable
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.eventos, state.dados.id_evento)}
                                    onChange={(op) => alterarSelect("id_evento", op)}
                                />
                            </Col>
                            <Col md={3}>
                                <LabelNovo label="Categoria" onClick={() => setShowCategoriaModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.categorias} styles={selectStyles} isClearable
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.categorias, state.dados.id_categoria)}
                                    onChange={(op) => alterarSelect("id_categoria", op)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Subcategoria</Form.Label>
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.subcategorias} styles={selectStyles} isClearable
                                    isDisabled={somenteLeitura || !state.dados.id_categoria}
                                    value={encontrarOpcao(opcoes.subcategorias, state.dados.id_subcategoria)}
                                    onChange={(op) => alterarSelect("id_subcategoria", op)}
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="label-admin">Descrição / Resumo</Form.Label>
                                <Form.Control as="textarea" rows={3} className="input-moderno" name="descricao" value={state.dados.descricao} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="label-admin">Contexto Histórico</Form.Label>
                                <Form.Control as="textarea" rows={3} className="input-moderno" name="contexto_historico" value={state.dados.contexto_historico} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>
                        </Row>

                        <div className="form-section-header mb-4">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-teal" />
                            <span>Gestão Administrativa e Direitos</span>
                        </div>
                        <Row className="g-4">
                            <Col md={6}>
                                <LabelNovo label="Direito Autoral / Licença" onClick={() => setShowDireitoModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.direitos} styles={selectStyles} placeholder="Selecione a licença..." isClearable
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.direitos, state.dados.id_direito_autoral)}
                                    onChange={(op) => alterarSelect("id_direito_autoral", op)}
                                />
                            </Col>
                            <Col md={6}>
                                <LabelNovo label="Acervo" onClick={() => setShowAcervoModal(true)} />
                                <Select menuPortalTarget={document.body}
                                    options={opcoes.acervos} styles={selectStyles} placeholder="Selecione o acervo..." isClearable
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.acervos, state.dados.id_acervo_detentor)}
                                    onChange={(op) => alterarSelect("id_acervo_detentor", op)}
                                />
                                <Form.Text className="text-muted small">
                                    Quem detém a guarda do original (ex.: um departamento), mesmo quando o autor é outro.
                                </Form.Text>
                            </Col>
                            <Col md={12}>
                                <Form.Label className="label-admin">Localização Física (Sala / Estante / Prateleira / Caixa / Envelope)</Form.Label>
                                <LocalizacaoSelector
                                    niveis={["sala", "estante", "prateleira", "caixa", "envelope_pasta"]}
                                    valores={localizacaoSelecionada}
                                    onChange={(v) => {
                                        setLocalizacaoSelecionada(v);
                                        setState((prev) => ({ ...prev, dados: { ...prev.dados, id_localizacao: v.id_localizacao } }));
                                    }}
                                    disabled={somenteLeitura}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Origem / Proveniência</Form.Label>
                                <Form.Select className="input-moderno" name="origem_proveniencia" value={state.dados.origem_proveniencia} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="">Selecione...</option>
                                    {OPCOES_ORIGEM.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Data de Entrada</Form.Label>
                                <Form.Control type="date" className="input-moderno" name="data_entrada_acervo" value={state.dados.data_entrada_acervo ?? ""} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Status</Form.Label>
                                <Form.Select className="input-moderno" name="status_fotografia" value={state.dados.status_fotografia} onChange={alterarCampo} disabled={somenteLeitura}>
                                    <option value="ativo">Ativo</option>
                                    <option value="inativo">Inativo</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="label-admin">Nº Inventário Assoc.</Form.Label>
                                <Form.Control className="input-moderno" name="numero_inventario_associado" value={state.dados.numero_inventario_associado} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>
                        </Row>

                        {!somenteLeitura && (
                            <div className="d-flex justify-content-end mt-5 pt-4 border-top">
                                <Button className="btn-apply-filters px-5 fw-bold" onClick={salvar}>
                                    <FontAwesomeIcon icon={faSave} className="me-2" /> Salvar Registro Iconográfico
                                </Button>
                            </div>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            {/* MODAL PESSOA RETRATADA */}
            <Modal show={showPessoaModal} onHide={() => setShowPessoaModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5"><FontAwesomeIcon icon={faUsers} className="me-2 text-teal" /> Cadastrar Pessoa Retratada</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form>
                        <Row className="g-3">
                            <Col md={12}><Form.Label className="fw-bold small">Nome Completo</Form.Label><Form.Control className="input-moderno" value={novaPessoa.nome} onChange={(e) => setNovaPessoa({ ...novaPessoa, nome: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Cargo / Função</Form.Label><Form.Control className="input-moderno" value={novaPessoa.cargo_funcao} onChange={(e) => setNovaPessoa({ ...novaPessoa, cargo_funcao: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Instituição</Form.Label><Form.Control className="input-moderno" value={novaPessoa.instituicao} onChange={(e) => setNovaPessoa({ ...novaPessoa, instituicao: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Departamento</Form.Label><Form.Control className="input-moderno" value={novaPessoa.departamento} onChange={(e) => setNovaPessoa({ ...novaPessoa, departamento: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Período de Atuação</Form.Label><Form.Control className="input-moderno" placeholder="Ex: 1950 - 1960" value={novaPessoa.periodo_atuacao} onChange={(e) => setNovaPessoa({ ...novaPessoa, periodo_atuacao: e.target.value })} /></Col>
                            <Col md={12}><Form.Label className="fw-bold small">Observações</Form.Label><Form.Control as="textarea" rows={2} className="input-moderno" value={novaPessoa.observacoes} onChange={(e) => setNovaPessoa({ ...novaPessoa, observacoes: e.target.value })} /></Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowPessoaModal(false)}>Cancelar</Button>
                    <Button variant="teal" onClick={salvarNovaPessoa}>Salvar Pessoa</Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL AUTOR */}
            <Modal show={showAutorModal} onHide={() => setShowAutorModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5">Cadastrar Novo Autor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3"><Form.Label className="fw-bold small">Nome do Autor / Fotógrafo</Form.Label><Form.Control className="input-moderno" value={novoAutor.nome} onChange={(e) => setNovoAutor({ ...novoAutor, nome: e.target.value })} /></Form.Group>
                        <Form.Group className="mb-3"><Form.Label className="fw-bold small">Nacionalidade</Form.Label><Form.Control className="input-moderno" value={novoAutor.nacionalidade} onChange={(e) => setNovoAutor({ ...novoAutor, nacionalidade: e.target.value })} /></Form.Group>
                        <Row>
                            <Col md={6}><Form.Label className="fw-bold small">Nascimento</Form.Label><Form.Control type="date" className="input-moderno" value={novoAutor.data_nascimento} onChange={(e) => setNovoAutor({ ...novoAutor, data_nascimento: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Falecimento</Form.Label><Form.Control type="date" className="input-moderno" value={novoAutor.data_falecimento} onChange={(e) => setNovoAutor({ ...novoAutor, data_falecimento: e.target.value })} /></Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="teal" className="w-100" onClick={salvarNovoAutor}>Cadastrar Autor</Button>
                </Modal.Footer>
            </Modal>


            {/* MODAL DIREITO AUTORAL */}
            <Modal show={showDireitoModal} onHide={() => setShowDireitoModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5">Cadastrar Direito Autoral / Licença</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3"><Form.Label className="fw-bold small">Nome do Detentor / Licença</Form.Label><Form.Control className="input-moderno" placeholder="Ex: Acervo FMRP-USP" value={novoDireito.nome_direito} onChange={(e) => setNovoDireito({ ...novoDireito, nome_direito: e.target.value })} /></Form.Group>
                        <Form.Group className="mb-3"><Form.Label className="fw-bold small">Condições de Uso Padrão</Form.Label><Form.Control as="textarea" rows={3} className="input-moderno" value={novoDireito.descricao} onChange={(e) => setNovoDireito({ ...novoDireito, descricao: e.target.value })} /></Form.Group>
                        <Form.Group className="mb-0">
                            <div className="p-3 rounded-4 border bg-light d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="mb-1 fw-bold small">Permitir Reprodução Público?</h6>
                                    <p className="mb-0 text-muted small">Define se a imagem pode ser baixada ou impressa pelos visitantes.</p>
                                </div>
                                <Form.Check
                                    type="switch"
                                    id="novo-direito-reproducao-switch"
                                    checked={novoDireito.permite_reproducao}
                                    onChange={(e) => setNovoDireito({ ...novoDireito, permite_reproducao: e.target.checked })}
                                />
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="teal" className="w-100" onClick={salvarNovoDireito}>Salvar Licença</Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL ACERVO */}
            <Modal show={showAcervoModal} onHide={() => setShowAcervoModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5"><FontAwesomeIcon icon={faBuildingColumns} className="me-2 text-teal" /> Cadastrar Acervo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small">Nome do Acervo</Form.Label>
                            <Form.Control className="input-moderno" placeholder="Ex: Departamento de Bioquímica" maxLength={200} value={novoAcervo.nome} onChange={(e) => setNovoAcervo({ ...novoAcervo, nome: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small">Tipo</Form.Label>
                            <Form.Select className="input-moderno" value={novoAcervo.tipo} onChange={(e) => setNovoAcervo({ ...novoAcervo, tipo: e.target.value })}>
                                {OPCOES_TIPO_ACERVO.map((v) => <option key={v} value={v}>{v}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-0">
                            <Form.Label className="fw-bold small">Observações</Form.Label>
                            <Form.Control as="textarea" rows={2} className="input-moderno" value={novoAcervo.observacoes} onChange={(e) => setNovoAcervo({ ...novoAcervo, observacoes: e.target.value })} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="teal" className="w-100" onClick={salvarNovoAcervo}>Salvar Acervo</Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL EVENTO */}
            <Modal show={showEventoModal} onHide={() => setShowEventoModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5">Cadastrar Evento Documentado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="g-3">
                            <Col md={12}><Form.Label className="fw-bold small">Nome do Evento</Form.Label><Form.Control className="input-moderno" value={novoEvento.nome_evento} onChange={(e) => setNovoEvento({ ...novoEvento, nome_evento: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Tipo de Evento</Form.Label>
                                <Form.Select className="input-moderno" value={novoEvento.tipo_evento} onChange={(e) => setNovoEvento({ ...novoEvento, tipo_evento: e.target.value })}>
                                    <option value="">Selecionar...</option>
                                    <option value="Cerimônia">Cerimônia</option>
                                    <option value="Congresso">Congresso</option>
                                    <option value="Aula">Aula</option>
                                    <option value="Visita">Visita</option>
                                    <option value="Outro">Outro</option>
                                </Form.Select>
                            </Col>
                            <Col md={6}><Form.Label className="fw-bold small">Local do Evento</Form.Label><Form.Control className="input-moderno" value={novoEvento.local_evento} onChange={(e) => setNovoEvento({ ...novoEvento, local_evento: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Data Início</Form.Label><Form.Control type="date" className="input-moderno" value={novoEvento.data_inicio} onChange={(e) => setNovoEvento({ ...novoEvento, data_inicio: e.target.value })} /></Col>
                            <Col md={6}><Form.Label className="fw-bold small">Data Fim</Form.Label><Form.Control type="date" className="input-moderno" value={novoEvento.data_fim} onChange={(e) => setNovoEvento({ ...novoEvento, data_fim: e.target.value })} /></Col>
                            <Col md={12}><Form.Label className="fw-bold small">Descrição / Contexto</Form.Label><Form.Control as="textarea" rows={3} className="input-moderno" value={novoEvento.descricao} onChange={(e) => setNovoEvento({ ...novoEvento, descricao: e.target.value })} /></Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="teal" className="w-100" onClick={salvarNovoEvento}>Salvar Evento</Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL CATEGORIA / SUBCATEGORIA */}
            <Modal show={showCategoriaModal} onHide={() => setShowCategoriaModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5">Nova Categoria / Subcategoria</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="p-3 bg-light rounded mb-3 border">
                            <Form.Label className="fw-bold small text-teal">Criar Nova Categoria</Form.Label>
                            <Form.Control className="input-moderno mb-2" placeholder="Nome da nova categoria" value={novaCategoria.nome_categoria} onChange={(e) => setNovaCategoria({ nome_categoria: e.target.value })} />
                            <Button variant="teal" size="sm" className="w-100" onClick={salvarNovaCategoria}>Criar Categoria</Button>
                        </div>
                        <div className="p-3 bg-light rounded border">
                            <Form.Label className="fw-bold small text-teal">Ou Adicionar Subcategoria a Existente</Form.Label>
                            <Select menuPortalTarget={document.body}
                                placeholder="Escolha a categoria pai..." styles={selectStyles} className="mb-2"
                                options={opcoes.categorias}
                                value={encontrarOpcao(opcoes.categorias, novaSubcategoria.id_categoria)}
                                onChange={(op) => setNovaSubcategoria({ ...novaSubcategoria, id_categoria: op?.value ?? "" })}
                            />
                            <Form.Control className="input-moderno mb-2" placeholder="Nome da subcategoria" value={novaSubcategoria.nome_subcategoria} onChange={(e) => setNovaSubcategoria({ ...novaSubcategoria, nome_subcategoria: e.target.value })} />
                            <Button variant="outline-teal" size="sm" className="w-100" onClick={salvarNovaSubcategoria}>Adicionar Subcategoria</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}
